import { FC, MouseEvent } from 'react';
import { useOutletContext } from 'react-router-dom';

import type { CommonOutletContext } from '@app/app/layouts';
import { LocalizedNavLink } from '@app/shared/ui/localized-link';
import { Button } from '@app/shared/ui/button';
import { cvaIcon } from '@app/shared/ui/elements';
import { BurgerButton } from '@app/shared/ui/burger-button';
import { useHeaderStore } from '@app/widgets/header/model/header.store';
import { usePaymentsDialogStore } from '@app/widgets/payments-dialog';
import {
  CogIcon,
  CreditCardIcon,
  CrownIcon,
  LogoutIcon,
  UserCircleIcon,
  WorldIcon,
} from '@app/components/ui/icons';
import { useLogOutMutation } from '@app/store/api/user.api';
import { route_path } from '@app/utils/route_path';
import { cn } from '@app/app/lib';

import { useTranslation, useLanguages } from '@app/app/i18n';

import { isBoxedMode } from '@app/utils/user-status.utils';

import { LanguageSwitcher } from '../components/language-switcher';

import { useNavLinks } from '../../model/static-data';

import { cvaButton, cvaProfileEmail, cvaSeparator, cvaBadge } from './header-popover-styles';
import { HeaderPopover } from './header-popover';

// Language switcher component
const LanguageSwitcherButton: FC = () => {
  const { currentLanguage, t: tHeader } = useTranslation('header');
  const languages = useLanguages('header');

  const currentLang = languages.find((lang) => lang.code === currentLanguage) || languages[0];

  return (
    <LanguageSwitcher
      itemTextSize="text-[14px]"
      trigger={
        <Button variant="secondary" size="text16" className={cvaButton()}>
          <WorldIcon className={cvaIcon()} />
          <span>{currentLang.flag}</span>
          <span>{tHeader('language.title')}</span>
        </Button>
      }
    />
  );
};

export const StageHeaderPopover: FC<{ daysLeft: number | null }> = ({ daysLeft }) => {
  const { t: tHeader } = useTranslation('header');
  const { context, user_ctx, setEmailNotify, setShowSettings, clearUserCtx, commonContext } =
    useOutletContext<CommonOutletContext>();

  const navLinks = useNavLinks();

  const { setIsOpen: setShowPayments } = usePaymentsDialogStore();

  const isBoxed = isBoxedMode(commonContext);
  const product_balance = context?.product_balance;

  const { toggleHeaderMenuOpen } = useHeaderStore();

  const closeHeaderMenu = () => toggleHeaderMenuOpen(false);

  const [logoutQuery, { isLoading: isLoadingLogoutMutation }] = useLogOutMutation();

  const logOutHandler = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    logoutQuery().then(() => {
      closeHeaderMenu();
      clearUserCtx();
      window.location.href = route_path.main;
    });
  };

  return (
    <HeaderPopover Trigger={<BurgerButton className="semiMd-max:ml-auto" />}>
      {/* Email */}
      {user_ctx?.username && (
        <div className={cvaProfileEmail()}>
          {product_balance?.is_active &&
          !product_balance?.product.is_trial &&
          product_balance.type === 'subscription' ? (
            <CrownIcon className={cn(cvaIcon(), 'text-secondary_warning_SI_400_warning')} />
          ) : (
            <UserCircleIcon className={cvaIcon()} />
          )}

          <span className="truncate">{user_ctx.username}</span>
        </div>
      )}
      {/* Confirm email button */}
      {!user_ctx?.is_confirmed && (
        <button type="button" onClick={() => setEmailNotify(true)} className={cvaBadge()}>
          {tHeader('account.confirmEmail')}
        </button>
      )}
      {/* Бейдж о завершении триала/подписки */}
      {!product_balance?.is_active && product_balance?.product && (
        <LocalizedNavLink
          to="/pricing/"
          className={cvaBadge()}
          dangerouslySetInnerHTML={{
            __html: product_balance.product.is_trial
              ? tHeader('account.trialExpired')
              : tHeader('account.subscriptionExpired'),
          }}
        />
      )}
      {/* Бейдж с количеством дней до конца триала */}
      {daysLeft !== null && (
        <LocalizedNavLink to="/pricing/" className={cvaBadge()}>
          {daysLeft > 0
            ? tHeader('account.trialExpiresInDays', { days: daysLeft })
            : tHeader('account.trialExpiresToday')}
        </LocalizedNavLink>
      )}

      {/* Payments button */}
      {!isBoxed && (
        <Button
          variant="secondary"
          size="text16"
          onClick={() => setShowPayments(true)}
          className={cvaButton()}
          disabled={isLoadingLogoutMutation}
        >
          <CreditCardIcon className={cvaIcon()} />
          <span>
            {!product_balance?.is_active && product_balance?.product
              ? tHeader('account.upgradePlan')
              : tHeader('account.managePlan')}
          </span>
        </Button>
      )}

      {
        <Button
          variant="secondary"
          size="text16"
          onClick={() => setShowSettings(true)}
          className={cvaButton()}
          disabled={isLoadingLogoutMutation}
        >
          <CogIcon className={cvaIcon()} />
          <span>{tHeader('account.settings')}</span>
        </Button>
      }

      <hr className={cvaSeparator()} />

      {/* Language switcher */}
      <LanguageSwitcherButton />

      <hr className={cvaSeparator()} />

      {!isBoxed &&
        navLinks.map(({ to, title, id }) => {
          // Templates - статическая страница, используем обычную ссылку
          if (to === route_path.templates) {
            return (
              <Button
                key={id}
                variant="secondary"
                size="text16"
                className={cvaButton({ isLink: true })}
                asChild
              >
                <a href="/templates/">{title}</a>
              </Button>
            );
          }
          return (
            <Button
              key={id}
              variant="secondary"
              size="text16"
              className={cvaButton({ isLink: true })}
              asChild
            >
              <LocalizedNavLink to={to}>{title}</LocalizedNavLink>
            </Button>
          );
        })}

      {!isBoxed && (
        <>
          <Button variant="secondary" size="text16" className={cvaButton({ isLink: true })} asChild>
            <a href={route_path.blog}>{tHeader('links.blog')}</a>
          </Button>
        </>
      )}

      <hr className={cvaSeparator()} />

      <Button
        variant="secondary"
        size="text16"
        onClick={logOutHandler}
        className={cvaButton()}
        disabled={isLoadingLogoutMutation}
      >
        <LogoutIcon className={cvaIcon()} />
        <span>{tHeader('auth.logout')}</span>
      </Button>
    </HeaderPopover>
  );
};
